import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity';
import { fromApplication } from '../reducers';
import { getAllRoleItems } from './role.selectors';
import { UserProfile } from '@content-platform/web-angular-administration-shared-data-access-graph-api';
import { Application, Permission } from '../models';

export const getApplicationRootState: MemoizedSelector<
unknown,
fromApplication.State
> = createFeatureSelector<fromApplication.State>('application');
export const getApplicationState: MemoizedSelector<unknown, fromApplication.State> = createSelector(
  getApplicationRootState,
  (state) => state
);

const applicationSelectors = fromApplication.applicationAdapter.getSelectors(getApplicationState);
export const getAllApplicationItems: (state: unknown) => Application[] =
  applicationSelectors.selectAll;
export const getApplicationEntities: (state: unknown) => Dictionary<Application> =
  applicationSelectors.selectEntities;

/**
 * Selector to return the loaded property of the state
 */
export const getApplicationsLoaded: MemoizedSelector<unknown, boolean> = createSelector(
  getApplicationState,
  fromApplication.getApplicationsLoaded
);

/**
 * Selector to return the loading property of the state
 */
export const getApplicationsLoading: MemoizedSelector<unknown, boolean> = createSelector(
  getApplicationState,
  fromApplication.getApplicationsLoading
);
export const getApplicationUsersWithPermission: (
  id
) => MemoizedSelector<unknown, { userProfile: UserProfile; roleNames: string[] }[]> = (id) =>
  createSelector(getApplicationEntities, getAllRoleItems, (appEntities, roleItems) => {
    const app = appEntities[id];
    // const users: { [type: string]: { userProfile: UserProfile; roleNames: string[] } } = {};
    const users: {
      [type: string]: { userProfile: UserProfile; roleNames: string[]; permissions: Permission[] };
    } = {};
    for (const role of roleItems) {
      const foundAppAsAdmin = role.applicationAdminPrivileges.find(
        (appPriviledge) => appPriviledge.id === app.id
      );
      const hasPermissions =
        role.permissions.filter((rolePermission) =>
          app?.features.map((feature) => feature.id).includes(rolePermission.feature.id)
        ).length > 0;

      // Does role have admin priviledges on an app or 1 or more permissions, then
      // users would have access to the app (i.e. active user).
      if (foundAppAsAdmin || hasPermissions) {
        for (const userProfile of <UserProfile[]>role.users) {
          users[userProfile.objectId] = users[userProfile.objectId] || {
            userProfile,
            roleNames: [],
            permissions: [],
          };
          users[userProfile.objectId].roleNames.push(role.name);
          users[userProfile.objectId].permissions = role.permissions;
        }
      }
    }

    return Object.keys(users).map((userId) => users[userId]);
  });
export const getApplicationUsers: (
  id
) => MemoizedSelector<unknown, { userProfile: UserProfile; roleNames: string[] }[]> = (id) =>
  createSelector(getApplicationEntities, getAllRoleItems, (appEntities, roleItems) => {
    const app = appEntities[id];
    const users: { [type: string]: { userProfile: UserProfile; roleNames: string[] } } = {};
    for (const role of roleItems) {
      const foundAppAsAdmin = role.applicationAdminPrivileges.find(
        (appPriviledge) => appPriviledge.id === app.id
      );
      const hasPermissions =
        role.permissions.filter((rolePermission) =>
          app.features.map((feature) => feature.id).includes(rolePermission.feature.id)
        ).length > 0;

      // Does role have admin priviledges on an app or 1 or more permissions, then
      // users would have access to the app (i.e. active user).
      if (foundAppAsAdmin || hasPermissions) {
        for (const userProfile of <UserProfile[]>role.users) {
          users[userProfile.objectId] = users[userProfile.objectId] || {
            userProfile,
            roleNames: [],
          };
          users[userProfile.objectId].roleNames.push(role.name);
        }
      }
    }

    return Object.keys(users).map((userId) => users[userId]);
  });
